import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import "../assets/users.css";
import "../assets/panels.css";
import "../assets/Tickets.css";
import Loader from "../components/Loader";
import { CHANGE_EVENT } from "../lib/inspectionSyncEngine";
import { formatDateUK } from "../utils/dateUtils";

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

// Normalise pasted search values: invisible characters and Excel-style
// thousand separators survive copy/paste and would break ID lookups.
const normalizeTicketSearch = (value) => {
  let v = String(value)
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, "");
  if (/^\d{1,3}(,\d{3})+$/.test(v)) {
    v = v.replace(/,/g, "");
  }
  return v;
};

const allColumns = [
  { key: "id", label: "ID" },  // Hidden by default in Laravel
  { key: "created_at", label: "Submitted" },
  { key: "created_time", label: "Time" },
  { key: "status", label: "Status" },
  { key: "urgency", label: "Urgency" },
  { key: "deadline", label: "Deadline" },
  { key: "book_in_date", label: "Book In" },
  { key: "completed", label: "Completed" },
  { key: "type", label: "Job Type" },
  { key: "property_id", label: "Property ID" },  // Hidden by default in Laravel
  { key: "property", label: "Property Name" },
  { key: "description", label: "Description" },  // Hidden by default in Laravel
  { key: "postcode", label: "Postcode" },
  { key: "client_view", label: "Client view" },  // Hidden by default in Laravel - inline checkbox
  { key: "guardian_view", label: "Guardian view" },  // Hidden by default in Laravel - inline checkbox
  { key: "contractor_first", label: "C. First" },  // Hidden by default in Laravel
  { key: "contractor_last", label: "C. Last" },  // Hidden by default in Laravel
  { key: "contractor", label: "Contractor" },  // Hidden by default in Laravel
  { key: "cost_of_works", label: "Cost of works" },  // Hidden by default in Laravel
  { key: "assignedMember", label: "Assigned" },
  { key: "to_delete", label: "To Delete" },
];

const STATUS_CARDS = [
  { key: "request", label: "REQUEST", color: "#7397A7", statusId: 4 },  // Laravel: 'Request' => 4
  { key: "inaction", label: "INACTION", color: "#3389FF", statusId: 2 },  // Laravel: 'In action' => 2
  { key: "overdue", label: "OVERDUE", color: "#FF3333", statusId: "overdue" },  // Derived: In Action with deadline lapsed
  { key: "contr_compeleted", label: "CONTR. COMPLETED", color: "#48E38C", statusId: 7 },  // Laravel: 'Contractor Completed' => 7
  { key: "compeleted", label: "COMPLETED", color: "#58A279", statusId: 5 },  // Laravel: 'Completed' => 5
  { key: "declined", label: "DECLINED", color: "#6c757d", statusId: 6 },  // Laravel: 'Declined' => 6 (re-coloured to dark grey since red is now Overdue)
  { key: "all", label: "ALL", color: "#0a7280", statusId: "" },  // All tickets (no filter) - use empty string for consistent comparison
];

const Tickets = () => {

  const [searchParams] = useSearchParams();
  const { isGuardian, getGuardianId } = useAuth();

  // Check if current user is a guardian (for simplified UI)
  const isGuardianUser = isGuardian();

  // Get guardian ID from URL params (for admin viewing guardian's tickets) or from logged-in guardian
  const urlGuardianId = searchParams.get('guardian_id') || '';
  const loggedInGuardianId = isGuardianUser ? getGuardianId() : null;
  const guardianId = urlGuardianId || loggedInGuardianId || '';

  // Laravel-style filter preservation keys
  const FILTER_STORAGE_KEY = 'tickets_filters';
  const SCROLL_STORAGE_KEY = 'tickets_scroll_position';
  const PAGE_STORAGE_KEY = 'tickets_current_page';
  const COLUMNS_STORAGE_KEY = 'tickets_visible_columns';

  // Helper function to load saved filters from localStorage (Laravel session equivalent)
  const loadSavedFilters = () => {
    try {
      const savedFilters = localStorage.getItem(FILTER_STORAGE_KEY);
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        console.log('🔄 [FILTER RESTORE] Restoring saved filters from localStorage:', parsed);
        return parsed;
      }
    } catch (error) {
      console.warn('⚠️ [FILTER RESTORE] Failed to load saved filters:', error);
    }
    return null;
  };

  // Helper function to load saved pagination state
  const loadSavedPage = () => {
    try {
      const savedPage = localStorage.getItem(PAGE_STORAGE_KEY);
      if (savedPage) {
        const page = parseInt(savedPage, 10);
        console.log('🔄 [PAGE RESTORE] Restoring saved page:', page);
        return page;
      }
    } catch (error) {
      console.warn('⚠️ [PAGE RESTORE] Failed to load saved page:', error);
    }
    return 1;
  };

  // Columns that are staff-only and should be hidden for guardians
  const staffOnlyColumns = ['client_view', 'guardian_view', 'cost_of_works', 'assignedMember', 'to_delete', 'contractor_first', 'contractor_last', 'contractor'];

  // Helper function to load saved column visibility from localStorage
  const loadSavedColumns = () => {
    try {
      const savedColumns = localStorage.getItem(COLUMNS_STORAGE_KEY);
      if (savedColumns) {
        let parsed = JSON.parse(savedColumns);
        console.log('🔄 [COLUMNS RESTORE] Restoring saved column visibility:', parsed);

        // For guardians, filter out any staff-only columns that may have been saved
        if (isGuardianUser) {
          const filteredColumns = parsed.filter(col => !staffOnlyColumns.includes(col));
          console.log('🔄 [COLUMNS RESTORE] Filtered columns for guardian:', filteredColumns);
          // If no valid columns remain after filtering, return null to use defaults
          return filteredColumns.length > 0 ? filteredColumns : null;
        }

        return parsed;
      }
    } catch (error) {
      console.warn('⚠️ [COLUMNS RESTORE] Failed to load saved columns:', error);
    }
    return null;
  };

  // Load saved filters and columns FIRST before using in state initialization
  const savedFilters = loadSavedFilters();
  const savedColumns = loadSavedColumns();

  // Server-side pagination state - restore from localStorage if available
  const [tickets, setTickets] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(loadSavedPage());
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [totalRecords, setTotalRecords] = useState(0);
  const [draw, setDraw] = useState(1);
  const [search, setSearch] = useState((savedFilters?.search || "").trimStart());
  const [debouncedSearch, setDebouncedSearch] = useState((savedFilters?.search || "").trimStart());

  // Filters state - restore from localStorage if available (Laravel session equivalent)
  const [filterData, setFilterData] = useState({
    jobTypes: [],
    urgencies: [],
    propertyLocations: [],
    contractors: [],
    clients: [],
  });
  const [statusFilter, setStatusFilter] = useState(savedFilters?.statusFilter || "");
  const [certificates, setCertificates] = useState(savedFilters?.certificates || false);
  const [filters, setFilters] = useState(savedFilters?.filters || {
    created_at_from: "",
    created_at_to: "",
    date_estimated_completion_from: "",
    date_estimated_completion_to: "",
    book_in_date_from: "",
    book_in_date_to: "",
    type_id: "",
    urgency_id: "",
    location_id: "",
    contractor_id: "",
    client_id: "",
    assigned_member_id: "",
  });

  // UI state - Hide Laravel-hidden columns by default (id, property_id, description, client_view, guardian_view, contractor fields, cost_of_works)
  // Restore from localStorage if available
  // Staff default columns
  const staffDefaultColumns = [
    "id",
    "created_at",
    "created_time",
    "status",
    "urgency",
    "deadline",
    "book_in_date",
    "completed",
    "type",
    "property",
    "postcode",
    "assignedMember"
  ];
  // Guardian default columns - exclude staff-only columns (client_view, guardian_view, contractor fields, cost_of_works, assignedMember, to_delete)
  const guardianDefaultColumns = [
    "created_at",
    "created_time",
    "status",
    "urgency",
    "deadline",
    "book_in_date",
    "completed",
    "type",
    "property",
    "postcode"
  ];
  const defaultVisibleColumns = isGuardianUser ? guardianDefaultColumns : staffDefaultColumns;
  const [visibleColumns, setVisibleColumns] = useState(savedColumns || defaultVisibleColumns);
  const [showColDropdown, setShowColDropdown] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [deleteSelectedLoading, setDeleteSelectedLoading] = useState(false);
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [clientSearchTimeout, setClientSearchTimeout] = useState(null);
  const [clientNameDisplay, setClientNameDisplay] = useState("");
  const [showStatusDescriptions, setShowStatusDescriptions] = useState(false);

  // Status counts (fetched from backend in server-side pagination response)
  const [statusCounts, setStatusCounts] = useState({
    request: 0,
    inaction: 0,
    overdue: 0,
    contr_compeleted: 0,
    compeleted: 0,
    declined: 0,
    building_setup: 0,
    all: 0
  });

  const totalPages = Math.ceil(totalRecords / pageSize);

  // Track if user manually clicked a sort column (to prevent auto-sort override)
  const [userManuallySorted, setUserManuallySorted] = useState(false);

  // Track if this is the initial mount to prevent page reset when restoring saved search
  const isInitialMount = useRef(true);
  // Monotonic ID for each loadTickets() call so stale responses (which can
  // arrive after a newer, faster search) are ignored instead of overwriting
  // the current result set.
  const ticketRequestIdRef = useRef(0);

  // Column mapping for server-side sorting
  const getSortColumnIndex = (field) => {
    const mapping = {
      "id": 0,                    // ticket.id
      "created_at": 5,            // ticket.created_at (submitted date)
      "created_time": 5,          // ticket.created_at (same as created_at)
      "status": 4,                // ticket.status_id -> status.name
      "urgency": 3,               // ticket.urgency_id -> urgency.name
      "deadline": 6,              // ticket.date_estimated_completion
      "book_in_date": 7,          // ticket.book_in_date
      "completed": 8,             // ticket.date_completed
      "type": 9,                  // service.name (Job Type)
      "property": 2,              // property.name
      "property_id": 15,          // property.property_id (short ID like "GGB0023")
      "postcode": 10,             // property.postcode
      "assignedMember": 11,       // assignedMember name (CONCAT)
      "contractor_first": 12,     // contractorContactUser.first_name
      "contractor_last": 13,      // contractorContactUser.last_name
      "contractor": 14,           // contractor.name
      "cost_of_works": 16,        // ticket.cost_of_works
      "title": 1,                 // ticket.description (legacy)
      "description": 1,           // ticket.description
    };
    const columnIndex = mapping[field] || 0;
    console.log(`🔍 [FRONTEND] Mapping sort field "${field}" to column index ${columnIndex}`);
    return columnIndex;
  };

  // Load tickets with server-side pagination
  const loadTickets = useCallback(() => {
    const requestId = ++ticketRequestIdRef.current;
    const startTime = performance.now();
    console.log(`🔍 [FRONTEND] Fetching tickets (request #${requestId})`);
    
    // Determine effective sort field based on active filters
    let effectiveSortField = sortField;
    let effectiveSortDirection = sortDirection;
    
    // If deadline filter is active, force sort by deadline (unless user manually sorted)
    if (!userManuallySorted && (filters.date_estimated_completion_from || filters.date_estimated_completion_to)) {
      effectiveSortField = "deadline";
      effectiveSortDirection = "desc";
      console.log('📊 [FRONTEND] Deadline filter active - forcing sort by deadline (desc)');
    }
    // If book-in date filter is active, force sort by book-in date (unless user manually sorted)
    else if (!userManuallySorted && (filters.book_in_date_from || filters.book_in_date_to)) {
      effectiveSortField = "book_in_date";
      effectiveSortDirection = "desc";
      console.log('📊 [FRONTEND] Book-in date filter active - forcing sort by book_in_date (desc)');
    }
    // If submitted date filter is active, force sort by created_at (unless user manually sorted)
    else if (!userManuallySorted && (filters.created_at_from || filters.created_at_to)) {
      effectiveSortField = "created_at";
      effectiveSortDirection = "desc";
      console.log('📊 [FRONTEND] Submitted date filter active - forcing sort by created_at (desc)');
    }
    
    console.log('📊 [FRONTEND] Current sort:', { sortField, sortDirection, effectiveSortField, effectiveSortDirection, userManuallySorted });
    setLoading(true);

    const datatableParams = {
      draw: draw,
      start: (currentPage - 1) * pageSize,
      length: pageSize,
      search: { value: debouncedSearch },
      order: [{ column: getSortColumnIndex(effectiveSortField), dir: effectiveSortDirection }],
      // Add all filters
      statusFilter: statusFilter,
      urgencyFilter: filters.urgency_id,
      jobTypeFilter: filters.type_id,
      locationFilter: filters.location_id,
      contractorFilter: filters.contractor_id,
      clientFilter: filters.client_id,
      assignedMemberFilter: filters.assigned_member_id,
      certificates: certificates ? 1 : '',
      guardian_id: guardianId, // Filter by guardian ID when viewing from Guardian Show page
      // Date filters
      created_at_from: filters.created_at_from,
      created_at_to: filters.created_at_to,
      date_estimated_completion_from: filters.date_estimated_completion_from,
      date_estimated_completion_to: filters.date_estimated_completion_to,
      book_in_date_from: filters.book_in_date_from,
      book_in_date_to: filters.book_in_date_to,
      // Visible columns for dynamic search
      visibleColumns: visibleColumns,
    };

    console.log("📊 [FRONTEND] Fetching tickets with params:", datatableParams);

    fetch(`${process.env.REACT_APP_API_URL}/api/tickets/datatable`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify(datatableParams)
    })
      .then((res) => res.json())
      .then((data) => {
        if (requestId !== ticketRequestIdRef.current) {
          console.log(`🔄 [FRONTEND] Ignoring stale tickets response #${requestId} (latest is #${ticketRequestIdRef.current})`);
          return;
        }
        const endTime = performance.now();
        const duration = (endTime - startTime).toFixed(0);
        console.log(`📊 [FRONTEND] DataTables response received in ${duration}ms:`, data);
        setTickets(data.data || []);
        setTotalRecords(data.recordsFiltered || 0);
        setDraw(data.draw);

        // Update status counts if provided
        if (data.statusCounts) {
          setStatusCounts(data.statusCounts);
        }

        setLoading(false);
        setInitialLoading(false);
      })
      .catch((error) => {
        if (requestId !== ticketRequestIdRef.current) return;
        console.error("❌ [FRONTEND] Error loading tickets:", error);
        setTickets([]);
        setTotalRecords(0);
        setLoading(false);
        setInitialLoading(false);
      });
  }, [currentPage, pageSize, debouncedSearch, sortField, sortDirection, statusFilter, certificates, filters.urgency_id, filters.type_id, filters.location_id, filters.contractor_id, filters.client_id, filters.assigned_member_id, filters.created_at_from, filters.created_at_to, filters.date_estimated_completion_from, filters.date_estimated_completion_to, filters.book_in_date_from, filters.book_in_date_to, draw, guardianId, userManuallySorted]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      // Only reset to page 1 if this is not the initial mount (i.e., user is actively searching)
      if (!isInitialMount.current) {
        setCurrentPage(1); // Reset to page 1 when search changes
      } else {
        isInitialMount.current = false;
      }
    }, 400); // 400ms debounce delay - wait for user to stop typing

    return () => clearTimeout(timer);
  }, [search]);

  // Load filter data and status overview on mount
  useEffect(() => {
    loadFilterData();
    loadStatusOverview();
  }, []);

  // Auto-update sort field when date filters are applied
  useEffect(() => {
    // If deadline filter is applied, always sort by deadline (unless user manually sorted by deadline already)
    if (filters.date_estimated_completion_from || filters.date_estimated_completion_to) {
      if (sortField !== "deadline" || !userManuallySorted) {
        console.log("🔄 Auto-sorting by deadline (latest to oldest)");
        setSortField("deadline");
        setSortDirection("desc");
        setUserManuallySorted(false); // Reset flag since we're auto-sorting
      }
    }
    // If book-in date filter is applied, always sort by book-in date (unless user manually sorted by book-in date already)
    else if (filters.book_in_date_from || filters.book_in_date_to) {
      if (sortField !== "book_in_date" || !userManuallySorted) {
        console.log("🔄 Auto-sorting by book-in date (latest to oldest)");
        setSortField("book_in_date");
        setSortDirection("desc");
        setUserManuallySorted(false); // Reset flag since we're auto-sorting
      }
    }
    // If submitted date filter is applied, always sort by created_at (unless user manually sorted by created_at already)
    else if (filters.created_at_from || filters.created_at_to) {
      if (sortField !== "created_at" || !userManuallySorted) {
        console.log("🔄 Auto-sorting by submitted date (latest to oldest)");
        setSortField("created_at");
        setSortDirection("desc");
        setUserManuallySorted(false); // Reset flag since we're auto-sorting
      }
    }
    // If no date filters are applied, reset to default sort
    else if (!filters.date_estimated_completion_from && !filters.date_estimated_completion_to &&
             !filters.book_in_date_from && !filters.book_in_date_to &&
             !filters.created_at_from && !filters.created_at_to) {
      if (sortField !== "created_at") {
        console.log("🔄 Resetting sort to default (created_at desc)");
        setSortField("created_at");
        setSortDirection("desc");
        setUserManuallySorted(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date_estimated_completion_from, filters.date_estimated_completion_to, filters.book_in_date_from, filters.book_in_date_to, filters.created_at_from, filters.created_at_to]);

  // Save filters to localStorage whenever they change (Laravel session equivalent)
  useEffect(() => {
    const filterState = {
      statusFilter,
      certificates,
      filters,
      search: debouncedSearch
    };
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterState));
      // Only log when filters are non-empty (reduce console spam)
      if (statusFilter || Object.values(filters).some(v => v) || debouncedSearch) {
        console.log('💾 [FILTER SAVE] Saved filters to localStorage');
      }
    } catch (error) {
      console.warn('⚠️ [FILTER SAVE] Failed to save filters:', error);
    }
  }, [statusFilter, certificates, filters, debouncedSearch, FILTER_STORAGE_KEY]);

  // Save current page to localStorage (Laravel pagination preservation)
  useEffect(() => {
    try {
      localStorage.setItem(PAGE_STORAGE_KEY, currentPage.toString());
      // Only log when page is not 1 (reduce console spam)
      if (currentPage !== 1) {
        console.log('💾 [PAGE SAVE] Saved page:', currentPage);
      }
    } catch (error) {
      console.warn('⚠️ [PAGE SAVE] Failed to save page:', error);
    }
  }, [currentPage, PAGE_STORAGE_KEY]);

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns));
      console.log('💾 [COLUMNS SAVE] Saved column visibility');
    } catch (error) {
      console.warn('⚠️ [COLUMNS SAVE] Failed to save columns:', error);
    }
  }, [visibleColumns, COLUMNS_STORAGE_KEY]);

  // Restore scroll position after initial data load (Laravel back button behavior)
  useEffect(() => {
    // Only restore scroll after initial loading is complete
    if (!initialLoading) {
      try {
        const savedScroll = localStorage.getItem(SCROLL_STORAGE_KEY);
        if (savedScroll) {
          // const scrollPosition = parseInt(savedScroll, 10);
          const scrollPosition = parseInt(1, 10);
          console.log('🔄 [SCROLL RESTORE] Restoring to position:', scrollPosition);
          // Use setTimeout to ensure DOM is fully rendered
          setTimeout(() => {
            window.scrollTo(0, scrollPosition);
            // Clear the saved scroll after restoring (single use)
            localStorage.removeItem(SCROLL_STORAGE_KEY);
            console.log('✅ [SCROLL RESTORE] Scroll position restored and cleared');
          }, 100);
        }
      } catch (error) {
        console.warn('⚠️ [SCROLL RESTORE] Failed to restore scroll:', error);
      }
    }
  }, [initialLoading]); // Run when initialLoading changes to false

  // Reload tickets when pagination/filters change
  useEffect(() => {
    loadTickets();
    loadStatusOverview(); // Update status cards when tickets change
  }, [currentPage, pageSize, debouncedSearch, sortField, sortDirection, statusFilter, certificates, filters.urgency_id, filters.type_id, filters.location_id, filters.contractor_id, filters.client_id, filters.assigned_member_id, filters.created_at_from, filters.created_at_to, filters.date_estimated_completion_from, filters.date_estimated_completion_to, filters.book_in_date_from, filters.book_in_date_to]);

  // Refresh the list when an offline ticket/document/note finishes syncing in
  // the background, so the new row appears without a manual page refresh.
  const loadTicketsRef = useRef(loadTickets);
  useEffect(() => {
    loadTicketsRef.current = loadTickets;
  }, [loadTickets]);
  useEffect(() => {
    let t = null;
    const onChange = () => {
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      // Debounce: the engine emits several change events during a sync run.
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        if (loadTicketsRef.current) loadTicketsRef.current();
      }, 600);
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      if (t) clearTimeout(t);
    };
  }, []);

  const loadFilterData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/filter-data`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Filter data received:", data);
      setFilterData(data);

      if (data.clients && data.clients.length > 0) {
        setClientSearchResults(data.clients);
      }
    } catch (err) {
      console.error("Failed to load filter data:", err);
      setFilterData({
        jobTypes: [],
        urgencies: [],
        propertyLocations: [],
        contractors: [],
        clients: [],
      });
    }
  };

  // Debounced client search
  const debouncedClientSearch = (query) => {
    if (clientSearchTimeout) {
      clearTimeout(clientSearchTimeout);
    }

    const timeout = setTimeout(() => {
      handleClientSearch(query);
    }, 300);

    setClientSearchTimeout(timeout);
  };

  const loadStatusOverview = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/status/overview`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("📊 Status overview received:", data);
      setStatusCounts(data);
    } catch (err) {
      console.error("Failed to load status overview:", err);
      // Keep default values (all zeros) on error
    }
  };

  const handleToggleClientView = async (ticketId, newValue) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/${ticketId}/toggle-client-view`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
          body: JSON.stringify({ client_view: newValue ? 1 : 0 })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update client view');
      }

      // Reload tickets to reflect the change
      loadTickets();
    } catch (error) {
      console.error('Error toggling client view:', error);
      alert('Failed to update client view. Please try again.');
    }
  };

  const handleToggleGuardianView = async (ticketId, newValue) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/${ticketId}/toggle-guardian-view`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
          body: JSON.stringify({ guardian_view: newValue ? 1 : 0 })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update guardian view');
      }

      // Reload tickets to reflect the change
      loadTickets();
    } catch (error) {
      console.error('Error toggling guardian view:', error);
      alert('Failed to update guardian view. Please try again.');
    }
  };

  const handleClientSearch = async (query) => {
    if (!query || query.trim().length === 0) {
      setClientSearchResults([]);
      setShowClientDropdown(false);
      return;
    }

    setClientSearchLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/clients/search?query=${encodeURIComponent(query)}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const clients = await response.json();
      setClientSearchResults(clients);
      setShowClientDropdown(clients.length > 0);
    } catch (err) {
      console.error("Failed to search clients:", err);
      setClientSearchResults([]);
      setShowClientDropdown(false);
    } finally {
      setClientSearchLoading(false);
    }
  };

  const handleClientSelect = (client) => {
    setFilters(prev => ({ ...prev, client_id: client.id }));
    setClientNameDisplay(client.name);
    setShowClientDropdown(false);
    setClientSearchResults([]);
  };

  const handleSort = (field) => {
    if (field === "to_delete") return; // Don't sort the checkbox column

    // Mark that user manually sorted
    setUserManuallySorted(true);
    
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
    console.log(`🔄 User manually sorted by: ${field} ${sortField === field ? (sortDirection === "asc" ? "desc" : "asc") : "asc"}`);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filtering

    if (key === "client_id" && value && value.trim().length > 0) {
      debouncedClientSearch(value);
    }
  };

  const handleTicketSelection = (ticketId, isChecked) => {
    setSelectedTickets((prev) => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(ticketId);
      } else {
        newSet.delete(ticketId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      const allTicketIds = tickets.map((ticket) => ticket.id);
      setSelectedTickets(new Set(allTicketIds));
    } else {
      setSelectedTickets(new Set());
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedTickets.size === 0) {
      alert("Please select tickets to delete");
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedTickets.size} selected ticket(s) and all their related data? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleteSelectedLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tickets/bulk-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          ticketsToDelete: Array.from(selectedTickets),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSelectedTickets(new Set());
          const { successful, failed } = data.results || {};
          if (successful !== undefined) {
            if (failed === 0) {
              alert(`Successfully deleted ${successful} tickets and all their related records!`);
            } else {
              alert(`Bulk delete completed: ${successful} successful, ${failed} failed.`);
            }
          }
          loadTickets(); // Refresh data
        } else {
          alert("Failed to delete tickets");
        }
      } else {
        alert("Failed to delete tickets");
      }
    } catch (error) {
      console.error("Error deleting tickets:", error);
      alert("Failed to delete tickets");
    } finally {
      setDeleteSelectedLoading(false);
    }
  };

  const handleRemove = (id) => {
    if (window.confirm("Are you sure you want to delete this ticket and all its related records? This action cannot be undone.")) {
      fetch(`${process.env.REACT_APP_API_URL}/api/tickets/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            alert(data.message);
            loadTickets(); // Refresh data
          } else {
            alert(data.message || "Failed to delete ticket");
          }
        })
        .catch(() => alert("Failed to delete ticket"));
    }
  };

  // Export functions - use backend API to export ALL tickets with filters
  const handleExportCSV = async () => {
    try {
      console.log("📥 Exporting tickets to CSV...");

      const exportParams = {
        statusFilter: statusFilter,
        urgencyFilter: filters.urgency_id,
        jobTypeFilter: filters.type_id,
        locationFilter: filters.location_id,
        contractorFilter: filters.contractor_id,
        clientFilter: filters.client_id,
        certificates: certificates ? 1 : '',
        search: { value: debouncedSearch },
        // Date filters
        created_at_from: filters.created_at_from,
        created_at_to: filters.created_at_to,
        date_estimated_completion_from: filters.date_estimated_completion_from,
        date_estimated_completion_to: filters.date_estimated_completion_to,
        book_in_date_from: filters.book_in_date_from,
        book_in_date_to: filters.book_in_date_to,
        // Assigned member filter
        assignedMemberFilter: filters.assigned_member_id,
        // Visible columns - only export columns that are visible
        visibleColumns: visibleColumns,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/export/tickets/csv`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
          body: JSON.stringify(exportParams)
        }
      );

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }

      // Get filename from response headers or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `tickets-export-${new Date().toISOString().split('T')[0]}.csv`;

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("✅ CSV export completed");
    } catch (error) {
      console.error("❌ CSV export failed:", error);
      alert("Failed to export CSV. Please try again.");
    }
  };

  const handleExportPDF = async () => {
    try {
      console.log("📥 Exporting tickets to PDF...");

      const exportParams = {
        statusFilter: statusFilter,
        urgencyFilter: filters.urgency_id,
        jobTypeFilter: filters.type_id,
        locationFilter: filters.location_id,
        contractorFilter: filters.contractor_id,
        clientFilter: filters.client_id,
        certificates: certificates ? 1 : '',
        search: { value: debouncedSearch },
        // Date filters
        created_at_from: filters.created_at_from,
        created_at_to: filters.created_at_to,
        date_estimated_completion_from: filters.date_estimated_completion_from,
        date_estimated_completion_to: filters.date_estimated_completion_to,
        book_in_date_from: filters.book_in_date_from,
        book_in_date_to: filters.book_in_date_to,
        // Assigned member filter
        assignedMemberFilter: filters.assigned_member_id,
        // Visible columns - only export columns that are visible
        visibleColumns: visibleColumns,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/export/tickets/pdf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
          body: JSON.stringify(exportParams)
        }
      );

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `tickets-export-${new Date().toISOString().split('T')[0]}.pdf`;

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("✅ PDF export completed");
    } catch (error) {
      console.error("❌ PDF export failed:", error);
      alert("Failed to export PDF. Please try again.");
    }
  };

  const handleExportExcel = async () => {
    try {
      console.log("📥 Exporting tickets to Excel...");

      const exportParams = {
        statusFilter: statusFilter,
        urgencyFilter: filters.urgency_id,
        jobTypeFilter: filters.type_id,
        locationFilter: filters.location_id,
        contractorFilter: filters.contractor_id,
        clientFilter: filters.client_id,
        certificates: certificates ? 1 : '',
        search: { value: debouncedSearch },
        // Date filters
        created_at_from: filters.created_at_from,
        created_at_to: filters.created_at_to,
        date_estimated_completion_from: filters.date_estimated_completion_from,
        date_estimated_completion_to: filters.date_estimated_completion_to,
        book_in_date_from: filters.book_in_date_from,
        book_in_date_to: filters.book_in_date_to,
        // Assigned member filter
        assignedMemberFilter: filters.assigned_member_id,
        // Visible columns - only export columns that are visible
        visibleColumns: visibleColumns,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/export/tickets/excel`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
          body: JSON.stringify(exportParams)
        }
      );

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `tickets-export-${new Date().toISOString().split('T')[0]}.xlsx`;

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("✅ Excel export completed");
    } catch (error) {
      console.error("❌ Excel export failed:", error);
      alert("Failed to export Excel. Please try again.");
    }
  };

  const handleColDropdownToggle = () => setShowColDropdown((v) => !v);
  const handleColDropdownBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setShowColDropdown(false);
  };

  // Clear all filters (Laravel "Reset" button behavior)
  const handleClearFilters = () => {
    console.log('🧹 [CLEAR FILTERS] Clearing all filters and resetting to defaults');

    // Clear all filter states
    setStatusFilter("");
    setCertificates(false);
    setFilters({
      created_at_from: "",
      created_at_to: "",
      date_estimated_completion_from: "",
      date_estimated_completion_to: "",
      book_in_date_from: "",
      book_in_date_to: "",
      type_id: "",
      urgency_id: "",
      location_id: "",
      contractor_id: "",
      client_id: "",
      assigned_member_id: "",
    });
    setSearch("");
    setDebouncedSearch("");
    setClientNameDisplay("");
    setCurrentPage(1);
    setSortField("created_at");
    setSortDirection("desc");
    setUserManuallySorted(false);

    // Clear saved filters from localStorage (Laravel session clear)
    try {
      localStorage.removeItem(FILTER_STORAGE_KEY);
      localStorage.removeItem(PAGE_STORAGE_KEY);
      localStorage.removeItem(SCROLL_STORAGE_KEY);
      console.log('🧹 [CLEAR FILTERS] Cleared localStorage filter state');
    } catch (error) {
      console.warn('⚠️ [CLEAR FILTERS] Failed to clear localStorage:', error);
    }
  };

  const isTicketOverdue = (ticket) => {
    const isInAction = ticket.status?.name?.toLowerCase().includes('in action') ||
                       ticket.status?.name?.toLowerCase().includes('inaction');
    if (!isInAction) return false;

    const deadline = ticket.date_estimated_completion;
    if (!deadline) return false;

    const deadlineDate = new Date(deadline);
    const now = new Date();

    return deadlineDate < now;
  };

  // Pagination controls
  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // Calculate showing entries range
    const startEntry = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endEntry = Math.min(currentPage * pageSize, totalRecords);

    return (
      <div className="pagination-container" style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        {/* Entries info - always shown */}
        <div style={{ fontSize: '14px' }}>
          Showing {startEntry} to {endEntry} of {totalRecords} entries
        </div>

        {/* Pagination buttons - only shown when more than 1 page */}
        {totalPages > 1 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{ padding: '5px 10px' }}
            >
              First
            </button>
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              style={{ padding: '5px 10px' }}
            >
              Previous
            </button>

            {startPage > 1 && (
              <>
                <button className="btn btn-sm" onClick={() => setCurrentPage(1)}>1</button>
                {startPage > 2 && <span style={{ padding: '5px' }}>...</span>}
              </>
            )}

            {pageNumbers.map((page) => (
              <button
                key={page}
                className={`btn btn-sm ${currentPage === page ? 'btn-primary' : 'btn-default'}`}
                onClick={() => setCurrentPage(page)}
                style={{
                  padding: '5px 10px',
                  backgroundColor: currentPage === page ? '#007bff' : 'transparent',
                  color: currentPage === page ? '#fff' : '#333',
                  fontWeight: currentPage === page ? 'bold' : 'normal'
                }}
              >
                {page}
              </button>
            ))}

            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && <span style={{ padding: '5px' }}>...</span>}
                <button className="btn btn-sm" onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
              </>
            )}

            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{ padding: '5px 10px' }}
            >
              Next
            </button>
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              style={{ padding: '5px 10px' }}
            >
              Last
            </button>

            <span style={{ marginLeft: '15px', fontSize: '14px' }}>
              Page {currentPage} of {totalPages}
            </span>
          </div>
        ) : null}
      </div>
    );
  };

  if (initialLoading) return <Loader />;

  return (
    <div className="tickets-page-container">
      {/* Breadcrumb */}
      <div className="breadcrumb-row">
        <a
          href="/dashboard"
          target="_blank" rel="noopener noreferrer"
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", textDecoration: 'none' }}
        >
          Dashboard
        </a>
        <div style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Ticket</span>
        </div>
      </div>

      {/* Activity Overview - Hidden for guardians (Laravel: only staff see status cards) */}
      {!isGuardianUser && (
        <>
          <div className="panel-title">
            <h3 className="mr-auto">Activity Overview</h3>
          </div>
          <div className="panel-body">
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "flex-start",
                justifyContent: "flex-start",
              }}
            >
              {STATUS_CARDS.map((card) => {
                // Use string comparison to handle number vs string type mismatch
                const isActive = String(statusFilter) === String(card.statusId);
                return (
                <div className="ticket-overview-container" key={card.key}>
                  <div className="ticket-overview-row">
                    <div
                      className={`ticket-overview-card ${isActive ? 'active' : 'all-active'}`}
                      style={{
                        background: card.color,
                        cursor: 'pointer',
                        border: isActive ? '3px solid #333' : 'none'
                      }}
                      onClick={() => {
                        console.log('🎫 [Tickets] Activity overview card clicked:', card.label);
                        // Always set the filter to the clicked card's statusId (no toggle - user expects consistent filter behavior)
                        setStatusFilter(card.statusId);
                        setCurrentPage(1);
                      }}
                    >
                      <div className="ticket-overview-value">
                        {statusCounts[card.key] || 0}
                      </div>
                      <div className="ticket-overview-label text-white overview-label" style={{ textAlign: "center", color: "white" }}>
                        {card.label}
                      </div>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          </div>
        </>
      )}

      {/* Status Description Panel - Hidden for guardians */}
      {!isGuardianUser && (
        <div className="panel panel-default" style={{ marginTop: '20px', marginBottom: '20px' }}>
          <div
            className="panel-heading panel-title"
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 15px',
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
            onClick={() => setShowStatusDescriptions(!showStatusDescriptions)}
          >
            <span style={{ fontWeight: 'bold', fontSize: '14px' }}>Descriptions of ticket statuses</span>
            <i
              className={`fa ${showStatusDescriptions ? 'fa-arrow-up' : 'fa-arrow-down'}`}
              aria-hidden="true"
              style={{ fontSize: '14px' }}
            ></i>
          </div>
          {showStatusDescriptions && (
            <div className="panel-body" style={{ padding: '15px', backgroundColor: '#fff', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>In action</strong> - Already pending ticket
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Request</strong> - Ticket reported by Guardian
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Completed</strong> - Completed job
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Declined</strong> - Rejected job
              </div>
              <div style={{ marginBottom: '0' }}>
                <strong>Contractor Completed</strong> - Contractor completed
              </div>
            </div>
          )}
        </div>
      )}

      {/* Calendar Section - Hidden for guardians */}
      {!isGuardianUser && (
        <div className="ticket_goto_calendar">
          <b>Go to user calendar:</b>
          <select
            className="form-control"
            onChange={(e) => {
              if (e.target.value) {
                window.open(`/tickets/member/${e.target.value}/calendar`, '_blank');
              }
            }}
          >
            <option value="">Please select calendar</option>
            {filterData.users && filterData.users.map(user => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {/* Filter Section - Simplified for guardians (Laravel: guardians only see header + add button) */}
      <form className="tickets-filter-section">
        <div className="tickets-maintenance-header">
          <b>Maintenance</b>
          <a
            href="/tickets/create"
            target="_blank" rel="noopener noreferrer"
            className="btn-add-user"
          >
            + Add New Ticket
          </a>
        </div>
        {/* Complex filters - Hidden for guardians */}
        {!isGuardianUser && (
          <div className="ticket_date_ticket_wrapper">
            <div className="ticket_date_ticket">
              <b style={{ width: "240px" }}>SUBMITTED DATE</b>
              <div className="ticket_date_ticket_from">
                <b>From</b>
                <input
                  className="form-control"
                  type="date"
                  value={filters.created_at_from}
                  onChange={(e) => handleFilterChange("created_at_from", e.target.value)}
                />
              </div>
              <div className="ticket_date_ticket_to">
                <b>To</b>
                <input
                  className="form-control"
                  type="date"
                  value={filters.created_at_to}
                  onChange={(e) => handleFilterChange("created_at_to", e.target.value)}
                />
              </div>
            </div>
            <div className="ticket_date_ticket" style={{ marginTop: "10px" }}>
              <b style={{ width: "240px" }}>EXPIRY DATE</b>
              <div className="ticket_date_ticket_from">
                <b>From</b>
                <input
                  className="form-control"
                  type="date"
                  value={filters.date_estimated_completion_from}
                  onChange={(e) => handleFilterChange("date_estimated_completion_from", e.target.value)}
                />
              </div>
              <div className="ticket_date_ticket_to">
                <b>To</b>
                <input
                  className="form-control"
                  type="date"
                  value={filters.date_estimated_completion_to}
                  onChange={(e) => handleFilterChange("date_estimated_completion_to", e.target.value)}
                />
              </div>
            </div>
            <div className="ticket_date_ticket" style={{ marginTop: "10px" }}>
              <b style={{ width: "240px" }}>BOOK-IN DATE</b>
              <div className="ticket_date_ticket_from">
                <b>From</b>
                <input
                  className="form-control"
                  type="date"
                  value={filters.book_in_date_from}
                  onChange={(e) => handleFilterChange("book_in_date_from", e.target.value)}
                />
              </div>
              <div className="ticket_date_ticket_to">
                <b>To</b>
                <input
                  className="form-control"
                  type="date"
                  value={filters.book_in_date_to}
                  onChange={(e) => handleFilterChange("book_in_date_to", e.target.value)}
                />
              </div>
            </div>
            <br />
            <div style={{ display: "flex" }}>
              <div className="ticket_date_ticket_job_type" style={{ marginTop: "10px" }}>
                <b style={{ width: "240px", fontSize: "14px", fontWeight: 700 }}>Job Type</b>
                <div>
                  <select
                    className="form-control"
                    style={{ width: "274px" }}
                    value={filters.type_id}
                    onChange={(e) => handleFilterChange("type_id", e.target.value)}
                  >
                    <option value="">All</option>
                    {filterData.jobTypes?.map((jobType) => (
                      <option key={jobType.id} value={jobType.id}>{jobType.name}</option>
                    )) || []}
                  </select>
                </div>
              </div>
              <div className="ticket_date_ticket_job_type" style={{ marginTop: "10px" }}>
                <b style={{ width: "240px", fontSize: "14px", fontWeight: 700 }}>Priority</b>
                <div>
                  <select
                    className="form-control"
                    style={{ width: "150px", marginRight: "100px" }}
                    value={filters.urgency_id}
                    onChange={(e) => handleFilterChange("urgency_id", e.target.value)}
                  >
                    <option value="">All</option>
                    {filterData.urgencies?.map((urgency) => (
                      <option key={urgency.id} value={urgency.id}>{urgency.name}</option>
                    )) || []}
                  </select>
                </div>
              </div>
            </div>
            <div style={{ display: "flex" }}>
              <div className="ticket_date_ticket_job_type" style={{ marginTop: "10px" }}>
                <b style={{ width: "240px", fontSize: "14px", fontWeight: 700 }}>Property Area</b>
                <div>
                  <select
                    className="form-control"
                    style={{ width: "150px", marginRight: "125px" }}
                    value={filters.location_id}
                    onChange={(e) => handleFilterChange("location_id", e.target.value)}
                  >
                    <option value="">All</option>
                    {filterData.propertyLocations?.map((location) => (
                      <option key={location.id} value={location.id}>{location.name}</option>
                    )) || []}
                  </select>
                </div>
              </div>
              <div className="ticket_date_ticket_job_type" style={{ marginTop: "10px" }}>
                <b style={{ width: "240px", fontSize: "14px", fontWeight: 700 }}>Contractor</b>
                <div>
                  <select
                    className="form-control"
                    style={{ width: "250px" }}
                    value={filters.contractor_id}
                    onChange={(e) => handleFilterChange("contractor_id", e.target.value)}
                  >
                    <option value="">All</option>
                    {filterData.contractors?.map((contractor) => (
                      <option key={contractor.id} value={contractor.id}>{contractor.name}</option>
                    )) || []}
                  </select>
                </div>
              </div>
              <div className="ticket_date_ticket_job_type" style={{ marginTop: "10px", marginLeft: "20px" }}>
                <b style={{ width: "240px", fontSize: "14px", fontWeight: 700 }}>Assigned To</b>
                <div>
                  <select
                    className="form-control"
                    style={{ width: "250px" }}
                    value={filters.assigned_member_id}
                    onChange={(e) => handleFilterChange("assigned_member_id", e.target.value)}
                  >
                    <option value="">All</option>
                    {filterData.users?.map((user) => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    )) || []}
                  </select>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div className="ticket_date_ticket_job_type" style={{ marginTop: "10px" }}>
                <div className="form-group">
                  <label className="control-label col-sm-3"><b>Client</b></label>
                  <div className="col-sm-9" style={{ position: "relative" }}>
                    <input
                      type="text"
                      name="client"
                      className="form-control"
                      placeholder="Search for clients..."
                      value={clientNameDisplay}
                      onChange={(e) => {
                        const value = e.target.value;
                        setClientNameDisplay(value);
                        // Clear the filter if user is typing (not using dropdown)
                        if (filters.client_id) {
                          handleFilterChange("client_id", "");
                        }
                        if (value && value.trim().length > 0) {
                          debouncedClientSearch(value);
                        } else {
                          setClientSearchResults([]);
                          setShowClientDropdown(false);
                        }
                      }}
                      onFocus={() => {
                        if (clientSearchResults.length > 0) {
                          setShowClientDropdown(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          if (!showClientDropdown) return;
                          setShowClientDropdown(false);
                        }, 200);
                      }}
                    />
                    {showClientDropdown && (
                      <div
                        className="client-search-dropdown"
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          maxHeight: "200px",
                          overflowY: "auto",
                          zIndex: 9999,
                          marginTop: "2px",
                          minWidth: "100%",
                          width: "100%"
                        }}
                      >
                        {clientSearchLoading ? (
                          <div className="text-center py-2 text-muted"><small>Searching clients...</small></div>
                        ) : clientSearchResults.length === 0 ? (
                          <div className="text-center py-2 text-muted"><small>No clients found</small></div>
                        ) : (
                          clientSearchResults.map((client) => (
                            <div
                              key={client.id}
                              className="client-option"
                              onClick={() => handleClientSelect(client)}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleClientSelect(client);
                              }}
                              style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                borderBottom: "1px solid #f0f0f0",
                                transition: "background-color 0.2s",
                                fontSize: "14px",
                                userSelect: "none"
                              }}
                              onMouseEnter={(e) => { e.target.style.backgroundColor = "#f8f9fa"; }}
                              onMouseLeave={(e) => { e.target.style.backgroundColor = "white"; }}
                            >
                              {client.name}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "8px", marginLeft: "auto" }}>
                <button
                  type="button"
                  className="btn-action certificates_show"
                  onClick={() => {
                    setCertificates(!certificates);
                    setCurrentPage(1);
                  }}
                  style={{
                    backgroundColor: certificates ? '#0a7280' : 'transparent',
                    color: certificates ? '#fff' : 'inherit',
                    fontWeight: certificates ? 'bold' : 'normal'
                  }}
                >
                  {certificates ? 'Show All Tickets' : 'Show Certificates'}
                </button>
                {selectedTickets.size > 0 && (
                  <span style={{ marginRight: "10px", fontSize: "14px", color: "#666", fontWeight: "500" }}>
                    {selectedTickets.size} selected
                  </span>
                )}
                <button
                  type="button"
                  className="btn-action ticket_remove_delete"
                  onClick={handleDeleteSelected}
                  disabled={deleteSelectedLoading || selectedTickets.size === 0}
                >
                  {deleteSelectedLoading ? "Deleting..." : "Delete selected"}
                </button>
              </div>
            </div>
          </div>
        )}
      </form>

      {/* Tickets Table */}
      <div className="users-table-panel">
        <div className="users-table-toolbar">
          <div className="users-table-entries">
            Show
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
              {[10, 25, 50, 100].map((n) => (<option key={n} value={n}>{n}</option>))}
            </select>
            entries
          </div>
          <div className="users-table-search ticket_search" style={{ position: 'relative' }}>
            Search:
            <input
              type="text"
              className="form-control"
              placeholder="Search tickets..."
              value={search}
              onChange={(e) => setSearch(normalizeTicketSearch(e.target.value))}
              style={{ paddingRight: loading ? '30px' : '12px' }}
            />
            {loading && (
              <div style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '12px',
                color: '#999'
              }}>
                ⏳
              </div>
            )}
          </div>
          {/* Export and admin buttons - Hidden for guardians */}
          {!isGuardianUser && (
            <>
              <button id="btn-export-csv-property" className="btn" onClick={handleExportCSV}>Export as CSV</button>
              <button id="btn-pdf-export-property" className="btn" onClick={handleExportPDF}>Export as PDF</button>
              <button id="btn-export-excel-property" className="btn" onClick={handleExportExcel}>Export as Excel</button>
              <button id="btn-print-property" className="btn" onClick={() => window.print()}>Print table</button>
              <button
                id="btn-clear-filters"
                className="btn"
                onClick={handleClearFilters}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  marginLeft: '10px'
                }}
                title="Clear all filters and reset to defaults"
              >
                Clear Filters
              </button>
            </>
          )}
          {/* Column visibility - Visible for all users including guardians */}
          <div style={{ position: "relative", display: "inline-block" }}>
            <button
              id="btn-visibility-property"
              className="btn"
              onClick={handleColDropdownToggle}
              tabIndex={0}
              style={{ minWidth: 120 }}
            >
              Column visibility
            </button>
            {showColDropdown && (
              <div className="column-visibility-dropdown" onBlur={handleColDropdownBlur}>
                <h4>Toggle Columns:</h4>
                {allColumns
                  // Filter columns for guardians - hide staff-only columns (use same list as staffOnlyColumns)
                  .filter((col) => {
                    if (isGuardianUser) {
                      return !staffOnlyColumns.includes(col.key);
                    }
                    return true; // Staff see all columns
                  })
                  .map((col) => (
                  <label
                    key={col.key}
                    onClick={() =>
                      setVisibleColumns((cols) =>
                        cols.includes(col.key)
                          ? cols.filter((c) => c !== col.key)
                          : [...cols, col.key]
                      )
                    }
                  >
                    <input type="checkbox" checked={visibleColumns.includes(col.key)} readOnly />
                    <span>{col.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="users-table-wrapper" style={{ position: 'relative' }}>
          {loading && tickets.length > 0 && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}>
              <div style={{ fontSize: '16px', color: '#333' }}>Loading...</div>
            </div>
          )}
          <table className="users-table" style={{ opacity: loading && tickets.length > 0 ? 0.5 : 1 }}>
            <thead>
              <tr>
                {allColumns.map(
                  (col) =>
                    // Hide staff-only columns for guardians
                    visibleColumns.includes(col.key) && !(isGuardianUser && staffOnlyColumns.includes(col.key)) && (
                      <th
                        key={col.key}
                        className={`text-nowrap ${col.key !== "to_delete" ? "sortable" : ""}`}
                        onClick={col.key !== "to_delete" ? () => handleSort(col.key) : undefined}
                        style={{ cursor: col.key !== "to_delete" ? "pointer" : "default" }}
                      >
                        {col.key === "to_delete" ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <input
                              type="checkbox"
                              checked={selectedTickets.size > 0 && selectedTickets.size === tickets.length}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                            {col.label}
                          </div>
                        ) : (
                          <>
                            {col.label}
                            <SortIcon direction={sortField === col.key ? sortDirection : null} isActive={sortField === col.key} />
                          </>
                        )}
                      </th>
                    )
                )}
                <th className="action-head text-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="text-center py-4 text-muted">
                    {statusFilter ? `No tickets found for selected status.` : "No tickets found matching your criteria."}
                  </td>
                </tr>
              ) : (
                tickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    style={{
                      backgroundColor: isTicketOverdue(ticket) ? '#dc3545' : 'transparent',
                      color: isTicketOverdue(ticket) ? '#fff' : 'inherit'
                    }}
                  >
                    {visibleColumns.includes("id") && (
                      <td>{ticket.id}</td>
                    )}
                    {visibleColumns.includes("created_at") && (
                      <td>{ticket.created_at ? formatDateUK(ticket.created_at) : ""}</td>
                    )}
                    {visibleColumns.includes("created_time") && (
                      <td>
                        {ticket.created_at
                          ? new Date(ticket.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                          : ""}
                      </td>
                    )}
                    {visibleColumns.includes("status") && (
                      <td>
                        <span
                          className="badge"
                          style={{
                            backgroundColor:
                              ticket.status?.name?.toLowerCase().includes('declined') ? '#FF3333' :
                              ticket.status?.name?.toLowerCase().includes('compeleted') ||
                              ticket.status?.name?.toLowerCase().includes('completed') ? 'rgb(88, 162, 121)' :
                              ticket.status?.name?.toLowerCase().includes('new') ? '#17a2b8' :
                              ticket.status?.name?.toLowerCase().includes('request') ? 'rgb(115, 151, 167)' :
                              '#007bff',
                            color: '#fff'
                          }}
                        >
                          {ticket.status?.name || "Unknown"}
                        </span>
                      </td>
                    )}
                    {visibleColumns.includes("urgency") && (
                      <td>
                        <span
                          className="badge"
                          style={{
                            backgroundColor:
                              ticket.urgency?.name?.toLowerCase() === 'medium' ? '#ffc107' :
                              ticket.urgency?.name?.toLowerCase() === 'low' ? '#17a2b8' :
                              ticket.urgency?.name?.toLowerCase() === 'high' ? '#dc3545' :
                              '#6c757d',
                            color: '#fff'
                          }}
                        >
                          {ticket.urgency?.name || "Unknown"}
                        </span>
                      </td>
                    )}
                    {visibleColumns.includes("deadline") && (
                      <td>
                        {ticket.date_estimated_completion
                          ? formatDateUK(ticket.date_estimated_completion)
                          : "Not specified"}
                      </td>
                    )}
                    {visibleColumns.includes("book_in_date") && (
                      <td>
                        {ticket.book_in_date
                          ? formatDateUK(ticket.book_in_date)
                          : "Not specified"}
                      </td>
                    )}
                    {visibleColumns.includes("completed") && (
                      <td>
                        {ticket.date_completed
                          ? formatDateUK(ticket.date_completed)
                          : "Not specified"}
                      </td>
                    )}
                    {visibleColumns.includes("type") && (
                      <td>{ticket.type?.name || "Not specified"}</td>
                    )}
                    {visibleColumns.includes("property_id") && (
                      <td>
                        <span
                          className="badge"
                          style={{
                            backgroundColor:
                              ticket.status?.name?.toLowerCase().includes('declined') ? '#FF3333' :
                              ticket.status?.name?.toLowerCase().includes('compeleted') ||
                              ticket.status?.name?.toLowerCase().includes('completed') ? 'rgb(88, 162, 121)' :
                              ticket.status?.name?.toLowerCase().includes('new') ? '#17a2b8' :
                              ticket.status?.name?.toLowerCase().includes('request') ? 'rgb(115, 151, 167)' :
                              '#007bff',
                            color: '#fff'
                          }}
                        >
                          {ticket.property?.property_id || ""}
                        </span>
                      </td>
                    )}
                    {visibleColumns.includes("property") && (
                      <td>{ticket.property?.name || "Not specified"}</td>
                    )}
                    {visibleColumns.includes("description") && (
                      <td style={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ticket.description || ""}
                      </td>
                    )}
                    {visibleColumns.includes("postcode") && (
                      <td>{ticket.property?.postcode || "Not specified"}</td>
                    )}
                    {/* Staff-only columns - hidden for guardians */}
                    {!isGuardianUser && visibleColumns.includes("client_view") && (
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={ticket.client_view === 1 || ticket.client_view === true}
                          onChange={(e) => handleToggleClientView(ticket.id, e.target.checked)}
                        />
                      </td>
                    )}
                    {!isGuardianUser && visibleColumns.includes("guardian_view") && (
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={ticket.guardian_view === 1 || ticket.guardian_view === true}
                          onChange={(e) => handleToggleGuardianView(ticket.id, e.target.checked)}
                        />
                      </td>
                    )}
                    {!isGuardianUser && visibleColumns.includes("contractor_first") && (
                      <td>{ticket.contractorContact?.first_name || ""}</td>
                    )}
                    {!isGuardianUser && visibleColumns.includes("contractor_last") && (
                      <td>{ticket.contractorContact?.last_name || ""}</td>
                    )}
                    {!isGuardianUser && visibleColumns.includes("contractor") && (
                      <td>{ticket.contractor?.name || ""}</td>
                    )}
                    {!isGuardianUser && visibleColumns.includes("cost_of_works") && (
                      <td>{ticket.cost_of_works ? `£${parseFloat(ticket.cost_of_works).toFixed(2)}` : ""}</td>
                    )}
                    {!isGuardianUser && visibleColumns.includes("assignedMember") && (
                      <td>
                        {ticket.assignedMember
                          ? `${ticket.assignedMember.first_name} ${ticket.assignedMember.last_name}`
                          : "No user"}
                      </td>
                    )}
                    {/* To Delete checkbox - Hidden for guardians */}
                    {!isGuardianUser && visibleColumns.includes("to_delete") && (
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={selectedTickets.has(ticket.id)}
                          onChange={(e) => handleTicketSelection(ticket.id, e.target.checked)}
                        />
                      </td>
                    )}
                    <td className="text-center">
                      <a
                        href={`/tickets/${ticket.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-action show"
                        onContextMenu={(e) => {
                          e.preventDefault();
                          window.open(`/tickets/${ticket.id}`, '_blank');
                        }}
                      >
                        Show
                      </a>
                      {/* Edit and Delete buttons - Hidden for guardians */}
                      {!isGuardianUser && (
                        <>
                          <a
                            href={`/tickets/${ticket.id}/edit`}
                            target="_blank" rel="noopener noreferrer"
                            className="btn-action edit"
                          >
                            Edit
                          </a>
                          <button className="btn-action remove" onClick={() => handleRemove(ticket.id)}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {renderPagination()}
      </div>
    </div>
  );
};

export default Tickets;
